import SignaturePad from "@/components/enrollment/SignaturePad";
import DocumentUploader, { PrivacyNotice } from "@/components/enrollment/DocumentUploader";
import { Check, CreditCard, CalendarClock } from "lucide-react";
import { useEffect, useState } from "react";
import { fetchPaymentsConfig } from "@/lib/enrollment/api";

// ─── Shared UI primitives ────────────────────────────────────────────────────
export const Field = ({ label, children, required, hint, testId }) => (
  <label className="block" data-testid={testId ? `field-${testId}` : undefined}>
    <span className="label-eyebrow text-[#1A1A1A]">
      {label}
      {required && <span className="text-[#CD7F32] ml-1">*</span>}
    </span>
    <div className="mt-2">{children}</div>
    {hint && <span className="block text-xs text-[#4B5563] mt-1.5">{hint}</span>}
  </label>
);

export const Input = ({ value, onChange, type = "text", placeholder, testId, ...rest }) => (
  <input
    type={type}
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    data-testid={testId}
    className="w-full bg-white border-2 border-[#E5E0D8] rounded-sm px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#1B3B2B] transition-colors"
    {...rest}
  />
);

export const Textarea = ({ value, onChange, placeholder, rows = 4, testId }) => (
  <textarea
    rows={rows}
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    data-testid={testId}
    className="w-full bg-white border-2 border-[#E5E0D8] rounded-sm px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#1B3B2B] transition-colors resize-y"
  />
);

export const Select = ({ value, onChange, options, testId, placeholder = "Select…" }) => (
  <select
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
    className="w-full bg-white border-2 border-[#E5E0D8] rounded-sm px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#1B3B2B] transition-colors"
  >
    <option value="" disabled>{placeholder}</option>
    {options.map((o) => (
      <option key={o.value || o} value={o.value || o}>{o.label || o}</option>
    ))}
  </select>
);

export const CheckboxRow = ({ checked, onChange, children, testId }) => (
  <label className="flex items-start gap-3 cursor-pointer group" data-testid={testId}>
    <span
      className={[
        "flex-shrink-0 mt-0.5 w-5 h-5 rounded-sm border-2 flex items-center justify-center transition-colors",
        checked ? "bg-[#1B3B2B] border-[#1B3B2B]" : "bg-white border-[#E5E0D8] group-hover:border-[#1B3B2B]",
      ].join(" ")}
    >
      {checked && <Check size={14} strokeWidth={3} className="text-white" />}
    </span>
    <input
      type="checkbox"
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      className="sr-only"
    />
    <span className="text-sm text-[#1A1A1A] leading-relaxed">{children}</span>
  </label>
);

export const SectionTitle = ({ eyebrow, title, lead }) => (
  <div className="mb-8 md:mb-10">
    {eyebrow && <div className="label-eyebrow text-[#CD7F32]">{eyebrow}</div>}
    <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#1B3B2B] leading-[1.05] tracking-tight mt-2">
      {title}
    </h1>
    {lead && <p className="mt-4 text-[#4B5563] text-base sm:text-lg leading-relaxed max-w-2xl">{lead}</p>}
  </div>
);

export const Card = ({ children, className = "" }) => (
  <div className={`bg-white border border-[#E5E0D8] rounded-sm p-6 sm:p-8 ${className}`}>{children}</div>
);

export const Divider = () => <div className="h-px bg-[#E5E0D8] my-8" />;

// ─── Payment plan selector (Full vs Split) ────────────────────────────────────
export function PaymentPlanSelector({ selected, onSelect }) {
  const [config, setConfig] = useState(null);
  useEffect(() => {
    fetchPaymentsConfig()
      .then((c) => setConfig(c))
      .catch(() => setConfig({ plans: { full: { available: true }, split: { available: false } } }));
  }, []);

  const OPTIONS = [
    {
      id: "full",
      icon: CreditCard,
      title: "Full Registration",
      amount: "$650",
      cadence: "One-time payment",
      pitch: "Pay in full today to instantly finalize enrollment. No further billing.",
      bullets: ["Charged once", "Enrollment finalized immediately", "No auto-renewal"],
    },
    {
      id: "split",
      icon: CalendarClock,
      title: "Split Registration Plan",
      amount: "$325",
      cadence: "First of 2 payments",
      pitch: "Pay the first half today; the second half automatically processes in 30 days.",
      bullets: ["$325 today", "$325 auto-charged in 30 days", "Same card on file"],
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="payment-plan-selector">
      {OPTIONS.map((opt) => {
        const active = selected === opt.id;
        const available = !config || config.plans?.[opt.id]?.available !== false;
        const Icon = opt.icon;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => available && onSelect(opt.id)}
            disabled={!available}
            data-testid={`payment-plan-${opt.id}`}
            className={[
              "text-left p-5 border-2 rounded-sm transition-all relative",
              active && available
                ? "border-[#1B3B2B] bg-white shadow-md"
                : available
                  ? "border-[#E5E0D8] bg-white/70 hover:border-[#1B3B2B]/40 cursor-pointer"
                  : "border-[#E5E0D8] bg-[#F4F1EA]/40 opacity-60 cursor-not-allowed",
            ].join(" ")}
          >
            {!available && (
              <span className="absolute top-3 right-3 inline-flex items-center px-2 py-0.5 bg-[#4B5563] text-white text-[9px] uppercase tracking-widest rounded-sm font-bold">
                Coming Soon
              </span>
            )}
            <div className="flex items-start gap-3">
              <div
                className={[
                  "flex-shrink-0 w-10 h-10 rounded-sm flex items-center justify-center",
                  active && available ? "bg-[#1B3B2B] text-white" : "bg-[#F4F1EA] text-[#1B3B2B]",
                ].join(" ")}
              >
                <Icon size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-serif-display text-xl text-[#1B3B2B] leading-tight">
                  {opt.title}
                </div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="font-serif-display text-2xl text-[#1B3B2B]">{opt.amount}</span>
                  <span className="text-xs uppercase tracking-widest text-[#4B5563] font-semibold">
                    {opt.cadence}
                  </span>
                </div>
              </div>
              <span
                className={[
                  "flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center mt-1",
                  active && available
                    ? "border-[#1B3B2B] bg-[#1B3B2B]"
                    : "border-[#E5E0D8] bg-white",
                ].join(" ")}
              >
                {active && available && <span className="w-2 h-2 rounded-full bg-white" />}
              </span>
            </div>
            <p className="text-sm text-[#4B5563] mt-3 leading-relaxed">{opt.pitch}</p>
            <ul className="mt-3 space-y-1.5">
              {opt.bullets.map((b) => (
                <li key={b} className="flex items-center gap-2 text-xs text-[#1A1A1A]">
                  <Check size={13} className="text-[#CD7F32] flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </button>
        );
      })}
    </div>
  );
}

// ─── Step renderers ──────────────────────────────────────────────────────────
export function WelcomeStep({ email, data, set }) {
  return (
    <div>
      <SectionTitle
        eyebrow="01 · Welcome"
        title="A serious place for serious students."
        lead="Scholar Athlete Academy trains the next generation of scholar-athletes. This portal collects your formal enrollment in fourteen brief, focused steps. Bring your student in close — the final two signatures belong to them."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="relative overflow-hidden rounded-sm border border-[#E5E0D8] aspect-[4/3] bg-[#0A0A0F]">
          <img
            src="/images/saa-classroom.jpeg"
            alt="Scholar Athlete Academy class in session"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: "contrast(1.08) saturate(1.14) brightness(1.02)" }}
          />
          {/* Editorial vignette + brand-tinted gradient */}
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.35) 100%)" }} />
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-[#1B3B2B]/85 via-[#1B3B2B]/15 to-transparent" />
          <div className="absolute bottom-5 left-5 right-5 text-white">
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-80 text-[#CD7F32] font-semibold">In Session</div>
            <div className="font-serif-display text-2xl mt-1 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">Where Discipline Meets Discovery</div>
          </div>
        </div>

        <Card className="flex flex-col">
          <div className="label-eyebrow">What to have ready</div>
          <ul className="mt-4 space-y-3 text-[#1A1A1A]">
            {[
              "Parent / guardian contact + emergency contact",
              "Student's date of birth, grade, and prior school",
              "Health insurance details & physician information",
              "Payment method for the $650 Enrollment Fee",
              "About 12 minutes of focused time",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <Check size={18} className="text-[#CD7F32] mt-0.5" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <div className="mt-auto pt-6">
            <div className="label-eyebrow">Saving as</div>
            <div className="font-serif-display text-xl text-[#1B3B2B] mt-1">{email}</div>
          </div>
        </Card>
      </div>

      {/* Welcome Letter — Strategic Director tone */}
      <Card className="mt-6 bg-gradient-to-br from-white to-[#F4F1EA]">
        <div className="label-eyebrow text-[#CD7F32]">A Letter From The Director</div>
        <h3 className="font-serif-display text-3xl text-[#1B3B2B] mt-2 leading-tight">
          Welcome to the Pursuit of Excellence
        </h3>
        <div className="mt-5 space-y-4 text-[#1A1A1A] leading-relaxed font-serif-display text-lg max-w-3xl">
          <p>Dear Family,</p>
          <p>
            Welcome to Scholar Athlete Academy. By choosing this path, you are making a profound
            commitment to your student-athlete's future. Our mission is to bridge the gap
            between elite physical performance and high-level academic mastery.
          </p>
          <p>
            At PRSVR, we don't just "check boxes." We build leaders. Whether your student is
            here for a Current Year track or a strategic Leap Year, they are now part of a
            structured, intentional environment designed to foster discipline, executive
            function, and a 5-star athletic profile.
          </p>
          <p>
            This enrollment portal is the first step in our partnership. Over the next
            fourteen steps, we will secure your child's academic records, protect their
            eligibility, prepare their Student Success Kit, and finalize your seat with
            the $650 Setup & Gear Fee at the end.
          </p>
          <p>We are honored to be a part of your athlete's journey. Let's get to work.</p>
        </div>
        <div className="mt-6 pt-6 border-t border-[#E5E0D8]">
          <div className="font-serif-display text-xl text-[#1B3B2B] italic">
            Margaret Williamson
          </div>
          <div className="text-xs uppercase tracking-widest text-[#4B5563] mt-1">
            Director of Academic Strategy · PRSVR Education
          </div>
        </div>
      </Card>

      {/* Referrer capture */}
      <Card className="mt-6">
        <div className="label-eyebrow text-[#CD7F32]">PRSVR Ambassador · Optional</div>
        <h3 className="font-serif-display text-2xl text-[#1B3B2B] mt-1">Who referred you?</h3>
        <p className="text-sm text-[#4B5563] mt-2 max-w-2xl">
          If a current PRSVR family or coach introduced you to the program, drop their name
          below — we track every introduction so we can thank them properly.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5">
          <Field label="Referrer Name" testId="referrer-name">
            <Input
              value={data.referrer_name}
              onChange={(v) => set("referrer_name", v)}
              placeholder="Family or coach name"
              testId="referrer-name-input"
            />
          </Field>
          <Field label="Referrer Email" testId="referrer-email">
            <Input
              type="email"
              value={data.referrer_email}
              onChange={(v) => set("referrer_email", v)}
              placeholder="optional"
              testId="referrer-email-input"
            />
          </Field>
        </div>
      </Card>
    </div>
  );
}

export function AgreementStep({ data, set }) {
  return (
    <div>
      <SectionTitle
        eyebrow="02 · Enrollment Agreement"
        title="The Agreement."
        lead="Please review the terms below. Acceptance is required to proceed."
      />
      <Card>
        <div className="prose max-w-none">
          <p className="text-[#1A1A1A] leading-relaxed">
            By enrolling at <strong>Scholar Athlete Academy</strong>, the undersigned parent or legal
            guardian agrees to the academic, athletic, and behavioral standards of the school.
            Tuition is committed for the full academic year. Withdrawal after the start of
            term does not release the family from financial obligation except as outlined in
            the school handbook.
          </p>
          <p className="text-[#1A1A1A] leading-relaxed mt-4">
            The student commits to full participation in the academic program, the assigned
            athletic discipline, and the community life of the school. The school commits, in
            return, to a learning environment of the highest standard, qualified coaches and
            instructors, and a safe residential and training facility.
          </p>
          <p className="text-[#1A1A1A] leading-relaxed mt-4">
            This Agreement is governed by the laws of the State of California. Any dispute
            shall be resolved through good-faith mediation before any other action.
          </p>
        </div>
        <Divider />
        <div className="space-y-4">
          <CheckboxRow
            checked={data.agreement_accepted}
            onChange={(v) => set("agreement_accepted", v)}
            testId="check-agreement"
          >
            I have read and agree to the terms of the Scholar Athlete Academy Enrollment Agreement.
          </CheckboxRow>
          <CheckboxRow
            checked={data.agreement_handbook_acknowledged}
            onChange={(v) => set("agreement_handbook_acknowledged", v)}
            testId="check-handbook"
          >
            I acknowledge receipt of the Family Handbook and will review it with my student.
          </CheckboxRow>
        </div>
      </Card>

      {/* Academic Disclaimer — Registrar tone */}
      <Card className="mt-6 border-l-4 border-l-[#CD7F32]">
        <div className="label-eyebrow text-[#CD7F32]">Academic Disclaimer</div>
        <h3 className="font-serif-display text-2xl text-[#1B3B2B] mt-1">
          Oversight & Development — Not Guaranteed Outcomes
        </h3>
        <p className="text-[#1A1A1A] leading-relaxed mt-3">
          Scholar Athlete Academy provides academic oversight, athletic development, executive-function
          coaching, and a structured environment designed to support the student-athlete's
          pursuit of collegiate opportunity. <strong>PRSVR does not guarantee NCAA eligibility,
          collegiate recruitment, scholarship awards, or any specific athletic or academic
          outcome.</strong> Final eligibility determinations are made by the NCAA Eligibility
          Center; recruitment decisions are made independently by college coaches and
          admissions offices. The family acknowledges that the student's results reflect a
          combination of the program's offerings and the student's individual effort, talent,
          and circumstances.
        </p>
        <div className="mt-5">
          <CheckboxRow
            checked={data.agreement_academic_disclaimer}
            onChange={(v) => set("agreement_academic_disclaimer", v)}
            testId="check-academic-disclaimer"
          >
            I have read and understand the Academic Disclaimer. I acknowledge that PRSVR
            provides oversight and development but does not guarantee NCAA eligibility or
            recruitment outcomes.
          </CheckboxRow>
        </div>
      </Card>
    </div>
  );
}

export function DocumentsStep({ email, data, set }) {
  const docs = [
    {
      type: "birth_certificate",
      label: "Birth Certificate",
      helper: "A clear scan or photo of your student's certificate of live birth (any state / country).",
    },
    {
      type: "immunization",
      label: "Immunization Record",
      helper: "California Form CDPH 286 or an equivalent vaccination history from your pediatrician.",
    },
    {
      type: "report_card",
      label: "Most Recent Report Card",
      helper: "Most recent term, semester, or trimester report from your student's current school.",
    },
  ];
  return (
    <div>
      <SectionTitle
        eyebrow="03 · Documents"
        title="Secure records upload."
        lead="Three required documents to complete enrollment. Uploaded over an encrypted connection and stored privately — only PRSVR administrators can view them."
      />

      <PrivacyNotice />

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {docs.map((d) => (
          <DocumentUploader
            key={d.type}
            email={email}
            docType={d.type}
            label={d.label}
            helper={d.helper}
            data={data}
            set={set}
          />
        ))}
      </div>

      <Card className="mt-6 border-l-4 border-l-[#CD7F32]">
        <CheckboxRow
          checked={data.docs_consent}
          onChange={(v) => set("docs_consent", v)}
          testId="check-docs-consent"
        >
          I confirm I am the parent or legal guardian, the documents I have uploaded
          relate to my own student, and I authorize Scholar Athlete Academy to retain them
          for enrollment verification and transcript coordination as described above.
        </CheckboxRow>
      </Card>
    </div>
  );
}

export function TuitionStep({ data, set, onPay, paying, enrollment, compFamily, compFamilyLabel }) {
  const paid =
    enrollment?.payment_status === "paid" || data.payment_status === "paid";
  const comped =
    !!compFamily ||
    enrollment?.payment_status === "comped" ||
    !!enrollment?.comp_family;

  const PATHS = {
    leap_year: {
      label: "Leap Year",
      tagline: "Full-program path · ready Aug 2026",
      tiers: [
        { id: "ly_t1", level: 1, label: "Tier 1 — Eligibility Only", annual: 650, monthly: 100 },
        { id: "ly_t2", level: 2, label: "Tier 2 — Academic + Curriculum", annual: 1500, monthly: 200 },
        { id: "ly_t3", level: 3, label: "Tier 3 — 5-Star Dev Lab", annual: 3000, monthly: 350 },
      ],
    },
    current_year: {
      label: "Current Year",
      tagline: "Mid-cycle enrollment · monthly only",
      tiers: [
        { id: "cy_t2", level: 2, label: "Tier 2 — Standard Curriculum", monthly: 399 },
        { id: "cy_t3", level: 3, label: "Tier 3 — Full SAA In-Person", monthly: 895 },
      ],
    },
  };

  const selectPath = (pathKey) => {
    if (data.tier_path === pathKey) return;
    set("tier_path", pathKey);
    set("tier_id", "");
    set("tier_label", "");
    set("tier_cadence", "");
    set("tier_rate_display", "");
  };

  const selectTier = (tier) => {
    set("tier_id", tier.id);
    set("tier_label", tier.label);
    // If only monthly available, lock cadence
    if (!tier.annual) {
      set("tier_cadence", "monthly");
      set("tier_rate_display", `$${tier.monthly} / month`);
    } else if (!data.tier_cadence) {
      // default to annual
      set("tier_cadence", "annual");
      set("tier_rate_display", `$${tier.annual} / year`);
    } else {
      const rate = data.tier_cadence === "annual" ? tier.annual : tier.monthly;
      set("tier_rate_display", `$${rate} / ${data.tier_cadence === "annual" ? "year" : "month"}`);
    }
  };

  const selectCadence = (cadence) => {
    set("tier_cadence", cadence);
    const path = PATHS[data.tier_path];
    const tier = path?.tiers.find((t) => t.id === data.tier_id);
    if (tier) {
      const rate = cadence === "annual" ? tier.annual : tier.monthly;
      set("tier_rate_display", `$${rate} / ${cadence === "annual" ? "year" : "month"}`);
    }
  };

  const path = data.tier_path ? PATHS[data.tier_path] : null;
  const tierLocked = path?.tiers.find((t) => t.id === data.tier_id);
  const canPay = data.tier_path && data.tier_id && data.tier_cadence;

  return (
    <div>
      <SectionTitle
        eyebrow="14 · Tuition"
        title="Reserve your seat."
        lead="A non-refundable $650 Setup & Gear Fee secures your student's roster spot and covers the Success Kit + administrative setup. This is the last step before signatures — review your tier, then pay to unlock the final Submit."
      />

      {/* Path selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {Object.entries(PATHS).map(([key, p]) => {
          const active = data.tier_path === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => selectPath(key)}
              data-testid={`tier-path-${key}`}
              className={[
                "text-left p-6 border-2 rounded-sm transition-all",
                active
                  ? "border-[#1B3B2B] bg-white shadow-md"
                  : "border-[#E5E0D8] bg-white/70 hover:border-[#1B3B2B]/40",
              ].join(" ")}
            >
              <div className="label-eyebrow text-[#CD7F32]">Path</div>
              <div className="font-serif-display text-2xl text-[#1B3B2B] mt-1">{p.label}</div>
              <div className="text-sm text-[#4B5563] mt-2">{p.tagline}</div>
              {active && (
                <div className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#1B3B2B] font-semibold">
                  <Check size={14} /> Selected
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Tier selection */}
      {path && (
        <div className="mt-8">
          <div className="label-eyebrow mb-4">Select Tier · {path.label}</div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {path.tiers.map((t) => {
              const active = data.tier_id === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => selectTier(t)}
                  data-testid={`tier-${t.id}`}
                  className={[
                    "text-left p-5 border-2 rounded-sm transition-all",
                    active
                      ? "border-[#1B3B2B] bg-white shadow-md"
                      : "border-[#E5E0D8] bg-white/70 hover:border-[#1B3B2B]/40",
                  ].join(" ")}
                >
                  <div className="text-xs uppercase tracking-widest text-[#CD7F32] font-semibold">
                    Tier {t.level}
                  </div>
                  <div className="font-serif-display text-xl text-[#1B3B2B] mt-1 leading-tight">
                    {t.label.split("—")[1]?.trim() || t.label}
                  </div>
                  <div className="mt-3 space-y-1 text-sm">
                    {t.annual && (
                      <div className="text-[#1A1A1A]">
                        <span className="font-semibold">${t.annual.toLocaleString()}</span>
                        <span className="text-[#4B5563]"> · annual</span>
                      </div>
                    )}
                    {t.monthly && (
                      <div className="text-[#1A1A1A]">
                        <span className="font-semibold">${t.monthly}</span>
                        <span className="text-[#4B5563]"> / month</span>
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Cadence selection — only when annual is also an option */}
      {tierLocked && tierLocked.annual && (
        <div className="mt-6">
          <div className="label-eyebrow mb-3">Payment Cadence</div>
          <div className="inline-flex border-2 border-[#E5E0D8] rounded-sm overflow-hidden">
            {["annual", "monthly"].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => selectCadence(c)}
                data-testid={`cadence-${c}`}
                className={[
                  "px-5 py-2.5 text-sm uppercase tracking-widest font-semibold transition-colors",
                  data.tier_cadence === c
                    ? "bg-[#1B3B2B] text-white"
                    : "bg-white text-[#1B3B2B] hover:bg-[#1B3B2B]/5",
                ].join(" ")}
              >
                {c === "annual"
                  ? `Annual ($${tierLocked.annual.toLocaleString()})`
                  : `Monthly ($${tierLocked.monthly})`}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Enrollment Fee payment */}
      {comped ? (
        <Card className="mt-8 border-2 border-emerald-600/40 bg-emerald-50/50" data-testid="tuition-comp-card">
          <div className="label-eyebrow text-emerald-700 mb-2">Setup & Gear Fee</div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="font-serif-display text-3xl text-emerald-800">
                ✓ Paid separately — no charge on this application
              </div>
              <div className="text-sm text-[#4B5563] mt-2 max-w-xl">
                <strong>{compFamilyLabel || enrollment?.comp_family_label || "Comp enrollment"}</strong>:
                the $650 Setup & Gear Fee has already been received outside this portal.
                Complete the remaining steps and submit — no payment step required.
              </div>
            </div>
            <div className="inline-flex items-center gap-2 bg-emerald-700 text-white rounded-sm px-6 py-3 font-semibold uppercase tracking-wider text-sm">
              <Check size={16} /> Fee Covered
            </div>
          </div>
        </Card>
      ) : (
        <Card className="mt-8 border-2 border-[#CD7F32]/30">
          <div className="label-eyebrow text-[#CD7F32] mb-4">Payment Options</div>
          <PaymentPlanSelector
            selected={data.selected_plan || "full"}
            onSelect={(p) => set("selected_plan", p)}
          />

          <Divider />
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="label-eyebrow text-[#CD7F32]">
                {data.selected_plan === "split" ? "Due Today · First of 2 Payments" : "Due Today · One-Time · Non-Refundable"}
              </div>
              <div className="font-serif-display text-4xl text-[#1B3B2B] mt-1">
                {data.selected_plan === "split" ? "$325.00" : "$650.00"}{" "}
                <span className="text-base font-sans-modern text-[#4B5563]">
                  USD · {data.selected_plan === "split" ? "First Installment" : "Setup & Gear Fee"}
                </span>
              </div>
              <div className="text-sm text-[#4B5563] mt-2 max-w-md">
                {data.selected_plan === "split" ? (
                  <>
                    First of two equal installments. The second <strong>$325</strong> will
                    automatically process 30 days from today using the same card. Covers the
                    full PRSVR Student Success Kit + administrative setup.
                  </>
                ) : (
                  <>
                    One-time setup and gear fee. Covers the PRSVR Student Success Kit (Polo,
                    Hoodie, HandleLife Performance Backpack, Scholar-Athlete Notebook), roster
                    registration, and administrative setup. Tier tuition is billed separately on
                    the schedule you selected above.
                  </>
                )}
              </div>
            </div>
            {paid ? (
              <div
                className="inline-flex items-center gap-2 bg-[#1B3B2B] text-white rounded-sm px-6 py-3 font-semibold uppercase tracking-wider text-sm"
                data-testid="payment-paid-badge"
              >
                <Check size={16} /> Setup & Gear Fee Paid
              </div>
            ) : (
              <button
                type="button"
                onClick={onPay}
                disabled={paying || !canPay}
                data-testid="pay-deposit-btn"
                className="inline-flex items-center justify-center bg-[#CD7F32] text-white hover:bg-[#B06A26] disabled:opacity-50 transition-colors rounded-sm px-7 py-3.5 font-semibold uppercase tracking-wider text-sm whitespace-nowrap"
              >
                {paying
                  ? "Redirecting…"
                  : data.selected_plan === "split"
                  ? "Pay $325 — First Installment"
                  : "Pay $650 Setup & Gear Fee"}
              </button>
            )}
          </div>
          {!canPay && !paid && (
            <p className="text-xs text-[#991B1B] mt-3">
              Select a path, tier{tierLocked?.annual ? ", and cadence" : ""} above to proceed with payment.
            </p>
          )}
        </Card>
      )}

      {/* Payment Authorization */}
      <Card className="mt-6">
        <div className="label-eyebrow text-[#CD7F32]">Payment Authorization · Required</div>
        <h3 className="font-serif-display text-2xl text-[#1B3B2B] mt-1">
          Monthly Tuition Policy
        </h3>
        <div className="mt-3 p-4 bg-[#F4F1EA] border-l-4 border-[#1B3B2B] rounded-sm">
          <p className="text-[#1A1A1A] leading-relaxed">
            <strong>Monthly tuition reserves your student's instructional placement and
            staffing allocation; it is due regardless of attendance.</strong> Tuition for
            scheduled sessions is committed once the month begins and is not prorated for
            absences, holidays, illness, or family travel.
          </p>
        </div>
        <div className="mt-6 space-y-4">
          <CheckboxRow
            checked={data.autopay_authorized}
            onChange={(v) => set("autopay_authorized", v)}
            testId="check-autopay"
          >
            <span>
              <strong>Autopay Authorization.</strong> I authorize Scholar Athlete Academy to charge
              my registered payment method automatically each month for the selected tier
              tuition until I terminate this authorization in writing.
            </span>
          </CheckboxRow>
          <CheckboxRow
            checked={data.withdrawal_30day_ack}
            onChange={(v) => set("withdrawal_30day_ack", v)}
            testId="check-30day-notice"
          >
            <span>
              <strong>30-Day Written Notice.</strong> I understand that withdrawal from
              the program requires a thirty (30) day written notice to the Registrar.
              Tuition for the notice period remains due, and the Setup & Gear Fee is
              non-refundable.
            </span>
          </CheckboxRow>
        </div>
        <Divider />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Billing Contact Name" testId="billing-name">
            <Input
              value={data.billing_name}
              onChange={(v) => set("billing_name", v)}
              placeholder="Same as parent name"
              testId="billing-name-input"
            />
          </Field>
          <Field label="Preferred Billing Day" testId="billing-day">
            <Select
              value={data.billing_day}
              onChange={(v) => set("billing_day", v)}
              options={["1st of month", "5th of month", "15th of month", "Last day of month"]}
              testId="billing-day-input"
            />
          </Field>
        </div>
      </Card>
    </div>
  );
}

export function ParentInfoStep({ data, set }) {
  return (
    <div>
      <SectionTitle
        eyebrow="04 · Parent / Guardian"
        title="Tell us who you are."
        lead="The primary contact for your student during the academic year."
      />
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Full Legal Name" required testId="parent-name">
            <Input value={data.parent_name} onChange={(v) => set("parent_name", v)} testId="parent-name-input" />
          </Field>
          <Field label="Relationship to Student" required testId="parent-relationship">
            <Select
              value={data.parent_relationship}
              onChange={(v) => set("parent_relationship", v)}
              options={["Mother", "Father", "Legal Guardian", "Other"]}
              testId="parent-relationship-input"
            />
          </Field>
          <Field label="Email" required testId="parent-email">
            <Input type="email" value={data.parent_email} onChange={(v) => set("parent_email", v)} testId="parent-email-input" />
          </Field>
          <Field label="Mobile Phone" required testId="parent-phone">
            <Input type="tel" value={data.parent_phone} onChange={(v) => set("parent_phone", v)} testId="parent-phone-input" />
          </Field>
          <Field label="Home Address" required testId="parent-address">
            <Textarea rows={2} value={data.parent_address} onChange={(v) => set("parent_address", v)} testId="parent-address-input" />
          </Field>
          <Field label="Occupation" testId="parent-occupation">
            <Input value={data.parent_occupation} onChange={(v) => set("parent_occupation", v)} testId="parent-occupation-input" />
          </Field>
        </div>
        <Divider />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Emergency Contact Name" required testId="emergency-contact">
            <Input value={data.emergency_contact} onChange={(v) => set("emergency_contact", v)} testId="emergency-contact-input" />
          </Field>
          <Field label="Emergency Contact Phone" required testId="emergency-phone">
            <Input type="tel" value={data.emergency_phone} onChange={(v) => set("emergency_phone", v)} testId="emergency-phone-input" />
          </Field>
        </div>
      </Card>
    </div>
  );
}

export function StudentInfoStep({ data, set }) {
  return (
    <div>
      <SectionTitle
        eyebrow="05 · Student"
        title="About your student."
        lead="Information about the enrolling student."
      />
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Full Legal Name" required testId="student-name">
            <Input value={data.student_name} onChange={(v) => set("student_name", v)} testId="student-name-input" />
          </Field>
          <Field label="Preferred Name" testId="student-preferred">
            <Input value={data.student_preferred} onChange={(v) => set("student_preferred", v)} testId="student-preferred-input" />
          </Field>
          <Field label="Date of Birth" required testId="student-dob">
            <Input type="date" value={data.student_dob} onChange={(v) => set("student_dob", v)} testId="student-dob-input" />
          </Field>
          <Field label="Gender" testId="student-gender">
            <Select
              value={data.student_gender}
              onChange={(v) => set("student_gender", v)}
              options={["Female", "Male", "Non-binary", "Prefer not to say"]}
              testId="student-gender-input"
            />
          </Field>
          <Field label="Grade Entering" required testId="student-grade">
            <Select
              value={data.student_grade}
              onChange={(v) => set("student_grade", v)}
              options={["6", "7", "8", "9", "10", "11", "12"]}
              testId="student-grade-input"
            />
          </Field>
          <Field label="Primary Sport / Discipline" required testId="student-sport">
            <Select
              value={data.student_sport}
              onChange={(v) => set("student_sport", v)}
              options={["Track & Field", "Basketball", "Soccer", "Tennis", "Swimming", "Volleyball", "Other"]}
              testId="student-sport-input"
            />
          </Field>
          <Field label="Previous School" testId="student-prev-school">
            <Input value={data.student_prev_school} onChange={(v) => set("student_prev_school", v)} testId="student-prev-school-input" />
          </Field>
          <Field label="GPA (last term)" testId="student-gpa">
            <Input value={data.student_gpa} onChange={(v) => set("student_gpa", v)} placeholder="e.g. 3.8" testId="student-gpa-input" />
          </Field>
        </div>
      </Card>
    </div>
  );
}

export function MedicalStep({ data, set }) {
  return (
    <div>
      <SectionTitle
        eyebrow="06 · Medical"
        title="Health & wellness."
        lead="Required for participation in athletic training and residential life."
      />
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Known Allergies" testId="medical-allergies">
            <Textarea rows={2} value={data.medical_allergies} onChange={(v) => set("medical_allergies", v)} placeholder="None, or list here" testId="medical-allergies-input" />
          </Field>
          <Field label="Current Medications" testId="medical-medications">
            <Textarea rows={2} value={data.medical_medications} onChange={(v) => set("medical_medications", v)} placeholder="None, or list here" testId="medical-medications-input" />
          </Field>
          <Field label="Pre-existing Conditions" testId="medical-conditions">
            <Textarea rows={2} value={data.medical_conditions} onChange={(v) => set("medical_conditions", v)} placeholder="None, or list here" testId="medical-conditions-input" />
          </Field>
          <Field label="Primary Physician" testId="medical-physician">
            <Input value={data.medical_physician} onChange={(v) => set("medical_physician", v)} testId="medical-physician-input" />
          </Field>
          <Field label="Physician Phone" testId="medical-physician-phone">
            <Input type="tel" value={data.medical_physician_phone} onChange={(v) => set("medical_physician_phone", v)} testId="medical-physician-phone-input" />
          </Field>
          <Field label="Insurance Provider" required testId="medical-insurance">
            <Input value={data.medical_insurance} onChange={(v) => set("medical_insurance", v)} testId="medical-insurance-input" />
          </Field>
          <Field label="Policy Number" required testId="medical-policy">
            <Input value={data.medical_policy} onChange={(v) => set("medical_policy", v)} testId="medical-policy-input" />
          </Field>
          <Field label="Last Physical Exam" testId="medical-last-physical">
            <Input type="date" value={data.medical_last_physical} onChange={(v) => set("medical_last_physical", v)} testId="medical-last-physical-input" />
          </Field>
        </div>
        <Divider />
        <CheckboxRow
          checked={data.medical_consent}
          onChange={(v) => set("medical_consent", v)}
          testId="check-medical-consent"
        >
          I consent to emergency medical treatment for my student in the event that I cannot
          be reached, and authorize school staff to act in loco parentis as needed.
        </CheckboxRow>
      </Card>
    </div>
  );
}

export function LiabilityStep({ data, set }) {
  return (
    <div>
      <SectionTitle
        eyebrow="07 · Liability"
        title="Assumption of Risk & Athletic Waiver."
        lead="California-compliant waiver. Read carefully — your acknowledgment is required to participate in physical training."
      />
      <Card>
        <div className="space-y-4 text-[#1A1A1A] leading-relaxed">
          <p>
            <strong>WAIVER, RELEASE, AND ASSUMPTION OF RISK AGREEMENT.</strong> This Agreement
            is entered into in the State of California by the undersigned parent or legal
            guardian, on behalf of themselves and the minor student named in this enrollment.
          </p>
          <p>
            <strong>1. Assumption of Risk.</strong> The undersigned understands and acknowledges
            that participation in athletic training, strength and conditioning, skill instruction,
            scrimmages, competition, on- and off-site travel, and residential life at The PRSVR
            School involves inherent and significant risk of physical injury, illness, permanent
            disability, paralysis, or death. These risks include but are not limited to: muscle
            strains and tears, ligament and joint injuries, fractures, concussion, heat-related
            illness, contact with surfaces or other participants, equipment failure, and exposure
            to communicable disease. The undersigned <strong>voluntarily and knowingly assumes
            all such risks</strong> on behalf of themselves and the minor student.
          </p>
          <p>
            <strong>2. Release of Claims.</strong> In consideration of the student's
            participation, the undersigned hereby releases, discharges, and agrees to hold
            harmless Scholar Athlete Academy, its trustees, directors, employees, coaches, volunteers,
            agents, and affiliates (collectively, "Released Parties") from any and all claims,
            demands, actions, or causes of action arising out of or related to participation,
            <strong> except for those arising from the gross negligence or willful misconduct
            of the Released Parties</strong>. This Release is intended to be as broad and
            inclusive as is permitted by the laws of the State of California.
          </p>
          <p>
            <strong>3. Medical Authorization.</strong> The undersigned authorizes the Released
            Parties to obtain or provide emergency medical care, including transportation to a
            licensed medical facility, when in the reasonable judgment of staff such care is
            needed and the family cannot be reached. The family remains financially responsible
            for medical costs.
          </p>
          <p>
            <strong>4. California Civil Code § 1542 Waiver.</strong> The undersigned expressly
            waives the protections of California Civil Code § 1542, which provides: "A general
            release does not extend to claims that the creditor or releasing party does not
            know or suspect to exist in his or her favor at the time of executing the release,
            and that, if known by him or her, would have materially affected his or her
            settlement with the debtor or released party."
          </p>
          <p>
            <strong>5. Severability & Governing Law.</strong> This Agreement is governed by the
            laws of the State of California. If any provision is held unenforceable, the
            remainder shall continue in full effect.
          </p>
        </div>
        <Divider />
        <div className="space-y-4">
          <CheckboxRow checked={data.liability_accepted} onChange={(v) => set("liability_accepted", v)} testId="check-liability">
            I have read this Waiver of Liability and Assumption of Risk in full and agree to
            its terms on behalf of myself and the enrolling student.
          </CheckboxRow>
          <CheckboxRow checked={data.liability_ca_assumption_risk} onChange={(v) => set("liability_ca_assumption_risk", v)} testId="check-liability-ca">
            I expressly waive the protections of California Civil Code § 1542 as described above.
          </CheckboxRow>
          <CheckboxRow checked={data.liability_insurance} onChange={(v) => set("liability_insurance", v)} testId="check-liability-insurance">
            I confirm that my student is covered by active medical insurance during the term
            of this enrollment.
          </CheckboxRow>
        </div>
      </Card>
    </div>
  );
}

export function MediaStep({ data, set }) {
  return (
    <div>
      <SectionTitle
        eyebrow="08 · Media"
        title="Photo & video release."
        lead="The school occasionally uses student imagery in athletic, academic, and admissions materials."
      />
      <Card>
        <div className="space-y-5">
          <CheckboxRow checked={data.media_release === true} onChange={(v) => set("media_release", v ? true : false)} testId="check-media-grant">
            I <strong>grant</strong> permission for Scholar Athlete Academy to photograph or record my
            student during school activities and to use those images in school publications,
            social media, and admissions materials.
          </CheckboxRow>
          <CheckboxRow checked={data.media_release === "deny"} onChange={(v) => set("media_release", v ? "deny" : "")} testId="check-media-deny">
            I <strong>do not grant</strong> permission for use of my student's image in school media.
          </CheckboxRow>
        </div>
        <Divider />
        <Field label="Additional restrictions or notes" testId="media-notes">
          <Textarea rows={3} value={data.media_notes} onChange={(v) => set("media_notes", v)} placeholder="Optional" testId="media-notes-input" />
        </Field>
      </Card>
    </div>
  );
}

export function AcademicReleaseStep({ data, set }) {
  const KNOWN_SCHOOLS = [
    "Fusion Academy Westlake",
    "Oaks Christian School",
    "Oaks Christian Online",
    "Fusion Academy (other campus)",
    "Public Charter School",
    "Public District School",
    "Private School",
    "Homeschool / Independent Study",
    "Other",
  ];
  return (
    <div>
      <SectionTitle
        eyebrow="09 · Academic Release"
        title="Records authorization & transfer."
        lead="Authorizes Scholar Athlete Academy and its Director of Academic Strategy to request transcripts and communicate directly with your student's prior or concurrent schools to coordinate academic records."
      />
      <Card>
        <p className="text-[#1A1A1A] leading-relaxed">
          The undersigned authorizes Scholar Athlete Academy and its designated academic staff,
          including the Director of Academic Strategy, to request, receive, review, and
          communicate with the schools listed below regarding the enrolling student's
          official records — including transcripts, attendance, standardized test scores,
          IEP/504 documentation (if applicable), disciplinary history, and any documents
          required for NCAA Eligibility Center review. This authorization remains in effect
          for the duration of the student's enrollment at PRSVR.
        </p>
        <Divider />
        <div className="label-eyebrow text-[#CD7F32]">Authorized Schools</div>
        <p className="text-sm text-[#4B5563] mt-1">
          List every school PRSVR may contact on the student's behalf. Common partners are
          pre-populated — edit or remove as needed.
        </p>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* School 1 */}
          <div className="border border-[#E5E0D8] rounded-sm p-4 bg-white/70">
            <div className="label-eyebrow">School 1</div>
            <div className="mt-3 space-y-4">
              <Field label="School Name" required testId="academic-school1-name">
                <Select
                  value={data.academic_school_1_name || "Fusion Academy Westlake"}
                  onChange={(v) => set("academic_school_1_name", v)}
                  options={KNOWN_SCHOOLS}
                  testId="academic-school1-name-input"
                />
              </Field>
              <Field label="Registrar / Contact Email" testId="academic-school1-email">
                <Input
                  type="email"
                  value={data.academic_school_1_email}
                  onChange={(v) => set("academic_school_1_email", v)}
                  testId="academic-school1-email-input"
                />
              </Field>
              <Field label="Student ID at this school" testId="academic-school1-id">
                <Input
                  value={data.academic_school_1_student_id}
                  onChange={(v) => set("academic_school_1_student_id", v)}
                  testId="academic-school1-id-input"
                />
              </Field>
            </div>
          </div>

          {/* School 2 */}
          <div className="border border-[#E5E0D8] rounded-sm p-4 bg-white/70">
            <div className="label-eyebrow">School 2</div>
            <div className="mt-3 space-y-4">
              <Field label="School Name" testId="academic-school2-name">
                <Select
                  value={data.academic_school_2_name || "Oaks Christian School"}
                  onChange={(v) => set("academic_school_2_name", v)}
                  options={KNOWN_SCHOOLS}
                  testId="academic-school2-name-input"
                />
              </Field>
              <Field label="Registrar / Contact Email" testId="academic-school2-email">
                <Input
                  type="email"
                  value={data.academic_school_2_email}
                  onChange={(v) => set("academic_school_2_email", v)}
                  testId="academic-school2-email-input"
                />
              </Field>
              <Field label="Student ID at this school" testId="academic-school2-id">
                <Input
                  value={data.academic_school_2_student_id}
                  onChange={(v) => set("academic_school_2_student_id", v)}
                  testId="academic-school2-id-input"
                />
              </Field>
            </div>
          </div>
        </div>

        <Field label="Additional Schools / Notes" testId="academic-additional" hint="List any other schools (charters, online programs, summer schools) and dates attended.">
          <Textarea
            rows={3}
            value={data.academic_additional_schools}
            onChange={(v) => set("academic_additional_schools", v)}
            placeholder="e.g. Albert Einstein Academy Charter — 2023–2024; Stanford Online Summer — 2024"
            testId="academic-additional-input"
          />
        </Field>

        <Divider />
        <div className="space-y-4">
          <CheckboxRow
            checked={data.academic_release}
            onChange={(v) => set("academic_release", v)}
            testId="check-academic-release"
          >
            I authorize Scholar Athlete Academy to request, receive, and review my student's complete
            academic records from the schools listed above.
          </CheckboxRow>
          <CheckboxRow
            checked={data.academic_communication_authorized}
            onChange={(v) => set("academic_communication_authorized", v)}
            testId="check-academic-communication"
          >
            I authorize Scholar Athlete Academy staff to communicate directly with registrars,
            counselors, and academic administrators at the schools listed above on matters
            related to my student's records and academic continuity.
          </CheckboxRow>
        </div>
      </Card>
    </div>
  );
}

export function ExpectationsStep({ data, set }) {
  const items = [
    { k: "expect_attendance", label: "Full attendance — academic & athletic — is non-negotiable." },
    { k: "expect_conduct", label: "On-campus, off-campus, and online conduct reflects the school's standards." },
    { k: "expect_substances", label: "Tobacco, alcohol, and recreational drugs are prohibited." },
    { k: "expect_communication", label: "Family will respond promptly to school communication regarding the student." },
    { k: "expect_handbook", label: "The Family Handbook governs day-to-day expectations and consequences." },
  ];
  return (
    <div>
      <SectionTitle
        eyebrow="10 · Expectations"
        title="The standard we keep."
        lead="A short, honest list — please affirm each item with your student present."
      />
      <Card>
        <div className="space-y-4">
          {items.map((it) => (
            <CheckboxRow
              key={it.k}
              checked={data[it.k]}
              onChange={(v) => set(it.k, v)}
              testId={`check-${it.k}`}
            >
              {it.label}
            </CheckboxRow>
          ))}
        </div>
        <Divider />
        <CheckboxRow checked={data.expectations_accepted} onChange={(v) => set("expectations_accepted", v)} testId="check-expectations-accepted">
          We have read and agree to Scholar Athlete Academy Code of Conduct in full.
        </CheckboxRow>
      </Card>
    </div>
  );
}

export function TechIntegrityStep({ data, set }) {
  return (
    <div>
      <SectionTitle
        eyebrow="11 · Tech Integrity"
        title="Acceptable use & AI policy."
        lead="A clear policy on devices, networks, and the responsible use of AI tools."
      />
      <Card>
        <p className="text-[#1A1A1A] leading-relaxed">
          The student agrees to use school networks and devices for educational and athletic
          purposes only. AI tools may be used as study aids — they may not be used to
          generate work submitted as the student's own. Violations of academic integrity,
          including unauthorized AI use, are treated with the same seriousness as plagiarism.
        </p>
        <Divider />
        <div className="space-y-4">
          <CheckboxRow checked={data.tech_aup_accepted} onChange={(v) => set("tech_aup_accepted", v)} testId="check-tech-aup">
            We agree to the Acceptable Use Policy for school networks and devices.
          </CheckboxRow>
          <CheckboxRow checked={data.tech_ai_policy_accepted} onChange={(v) => set("tech_ai_policy_accepted", v)} testId="check-tech-ai">
            We understand and agree to the AI & Academic Integrity Policy.
          </CheckboxRow>
          <CheckboxRow checked={data.tech_integrity_accepted} onChange={(v) => set("tech_integrity_accepted", v)} testId="check-tech-integrity">
            The student pledges to abide by the school's Tech Integrity standards in full.
          </CheckboxRow>
        </div>
      </Card>
    </div>
  );
}

export function UniformsStep({ data, set }) {
  const sizes = ["XS", "S", "M", "L", "XL", "XXL"];
  return (
    <div>
      <SectionTitle
        eyebrow="12 · Uniforms"
        title="SAA Student Success Kit."
        lead="Your $650 setup fee includes the full Student Success Kit, sized and shipped before orientation."
      />

      {/* Kit contents overview */}
      <Card>
        <div className="label-eyebrow text-[#CD7F32]">What's Included</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {[
            { name: "PRSVR Polo", purpose: "Professionalism and program identity.", sized: true },
            { name: "PRSVR Hoodie", purpose: "Identity and warmth for training days.", sized: true },
            { name: "HandleLife Academy Performance Backpack", purpose: "Daily utility for the student-athlete lifestyle.", sized: false },
            { name: "Scholar-Athlete Notebook", purpose: "Focused executive function and note-taking.", sized: false },
          ].map((item) => (
            <div key={item.name} className="flex items-start gap-3 p-4 border border-[#E5E0D8] rounded-sm bg-white">
              <div className="flex-shrink-0 mt-1 w-8 h-8 rounded-sm bg-[#1B3B2B] text-white flex items-center justify-center">
                <Check size={16} strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-semibold text-[#1A1A1A]">{item.name}</div>
                <div className="text-xs text-[#4B5563] mt-1">{item.purpose}</div>
                {item.sized && (
                  <div className="text-[10px] tracking-widest uppercase text-[#CD7F32] font-semibold mt-2">
                    Size required below
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Sized items */}
      <Card className="mt-6">
        <div className="label-eyebrow">Sizing — Polo & Hoodie</div>
        <p className="text-sm text-[#4B5563] mt-1">Adult unisex sizing. When in doubt, size up for the hoodie.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5">
          <Field label="PRSVR Polo Size" required testId="kit-polo">
            <Select
              value={data.success_kit_polo_size}
              onChange={(v) => set("success_kit_polo_size", v)}
              options={sizes}
              testId="kit-polo-input"
            />
          </Field>
          <Field label="PRSVR Hoodie Size" required testId="kit-hoodie">
            <Select
              value={data.success_kit_hoodie_size}
              onChange={(v) => set("success_kit_hoodie_size", v)}
              options={sizes}
              testId="kit-hoodie-input"
            />
          </Field>
        </div>
        <Divider />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Field label="Athletic Shoe Size (US)" testId="kit-shoe">
            <Input
              value={data.success_kit_shoe_size}
              onChange={(v) => set("success_kit_shoe_size", v)}
              placeholder="e.g. 9.5"
              testId="kit-shoe-input"
            />
          </Field>
          <Field label="Name on Kit" hint="Up to 14 characters" testId="kit-name">
            <Input
              value={data.success_kit_name}
              onChange={(v) => set("success_kit_name", v)}
              maxLength={14}
              testId="kit-name-input"
            />
          </Field>
          <Field label="Preferred Jersey Number" testId="kit-number">
            <Input
              value={data.success_kit_number}
              onChange={(v) => set("success_kit_number", v)}
              placeholder="00–99"
              testId="kit-number-input"
            />
          </Field>
        </div>
      </Card>
    </div>
  );
}

export function LoginsStep({ data, set }) {
  return (
    <div>
      <SectionTitle
        eyebrow="13 · Logins"
        title="Set up student access."
        lead="The student will receive credentials for the academic portal, learning systems, and athletic scheduling."
      />
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Preferred Student Username" required hint="Letters, numbers, dots." testId="login-username">
            <Input value={data.login_username} onChange={(v) => set("login_username", v.replace(/[^a-zA-Z0-9.]/g, ""))} testId="login-username-input" />
          </Field>
          <Field label="Student Email (school will create)" testId="login-suggested-email">
            <Input
              value={
                data.login_username
                  ? `${data.login_username}@prsvr.school`
                  : ""
              }
              onChange={() => {}}
              disabled
              testId="login-email-display"
            />
          </Field>
          <Field label="Backup Recovery Email (parent)" required testId="login-recovery">
            <Input type="email" value={data.login_recovery_email} onChange={(v) => set("login_recovery_email", v)} testId="login-recovery-input" />
          </Field>
          <Field label="Two-Factor Phone" testId="login-2fa">
            <Input type="tel" value={data.login_2fa_phone} onChange={(v) => set("login_2fa_phone", v)} testId="login-2fa-input" />
          </Field>
        </div>
        <Divider />
        <CheckboxRow checked={data.login_password_policy} onChange={(v) => set("login_password_policy", v)} testId="check-password-policy">
          I understand the student will set their own password upon first login and agree
          to the school's password & 2FA policy.
        </CheckboxRow>
      </Card>
    </div>
  );
}

export function OrientationStep({ data, set, onSubmit, submitting, submitted, enrollment }) {
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div>
      <SectionTitle
        eyebrow="15 · Orientation"
        title="Final signatures."
        lead="Review, sign, and submit. You'll receive a copy of your signed agreement by email."
      />

      {/* First Day Checklist — always visible, printable */}
      <Card className="mb-6 print:shadow-none">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="label-eyebrow text-[#CD7F32]">First Day Checklist</div>
            <h3 className="font-serif-display text-2xl text-[#1B3B2B] mt-1">
              Your student's first week at PRSVR.
            </h3>
          </div>
          <button
            type="button"
            onClick={() => window.print()}
            data-testid="print-checklist-btn"
            className="inline-flex items-center gap-2 border-2 border-[#1B3B2B] text-[#1B3B2B] hover:bg-[#1B3B2B] hover:text-white transition-colors rounded-sm px-4 py-2 text-xs uppercase tracking-widest font-semibold print:hidden"
          >
            Print
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Weekly schedule */}
          <div>
            <div className="label-eyebrow">Weekly Schedule</div>
            <table className="w-full text-sm mt-3 border-collapse">
              <tbody>
                {[
                  ["Mon", "Academic Block · Strength & Conditioning"],
                  ["Tue", "Academic Block · Skill Instruction"],
                  ["Wed", "Academic Block · Recovery + Film"],
                  ["Thu", "Academic Block · Scrimmage / Competition Prep"],
                  ["Fri", "Leadership Experience Lab (half-day)"],
                ].map(([day, plan]) => (
                  <tr key={day} className="border-b border-[#E5E0D8]">
                    <td className="py-2.5 pr-4 font-semibold text-[#1B3B2B] tabular-nums w-16">{day}</td>
                    <td className="py-2.5 text-[#1A1A1A]">{plan}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 p-3 bg-[#F4F1EA] border-l-4 border-[#CD7F32] rounded-sm">
              <div className="label-eyebrow">Friday Spotlight</div>
              <div className="font-serif-display text-lg text-[#1B3B2B] mt-0.5">
                Leadership Experience Lab
              </div>
              <p className="text-xs text-[#4B5563] mt-1 leading-relaxed">
                Guest speakers, mentorship circles, and applied executive-function workshops
                with PRSVR alumni and partner programs.
              </p>
            </div>
          </div>

          {/* Digital platforms */}
          <div>
            <div className="label-eyebrow">Digital Platforms · First Login</div>
            <ul className="mt-3 space-y-3 text-sm">
              {[
                ["IXL Learning", "Math, language arts, and adaptive diagnostics. Login emailed orientation week."],
                ["PRSVR Curriculum Portal", "Daily lesson plans, assignments, and grade book."],
                ["SAA Platform", "Athletic scheduling, training logs, and film review."],
                ["Family Communications", "Weekly digest + direct line to your student's Director of Academic Strategy."],
              ].map(([name, desc]) => (
                <li key={name} className="flex items-start gap-3">
                  <Check size={16} className="text-[#CD7F32] mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-[#1A1A1A]">{name}</div>
                    <div className="text-xs text-[#4B5563]">{desc}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-4 text-xs text-[#4B5563]">
              Student credentials will be issued to{" "}
              <strong className="text-[#1B3B2B]">
                {data.login_username ? `${data.login_username}@prsvr.school` : "your chosen username"}
              </strong>{" "}
              within 48 hours of submission.
            </div>
          </div>
        </div>

        <Divider />
        <div className="label-eyebrow">What to Bring · Day One</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 text-sm">
          {[
            "PRSVR Polo + Hoodie (shipped in your Success Kit)",
            "HandleLife Performance Backpack",
            "Scholar-Athlete Notebook + two pens",
            "Refillable water bottle",
            "Athletic shoes (training surface)",
            "Photo ID / school ID if returning from prior program",
          ].map((it) => (
            <div key={it} className="flex items-center gap-2 text-[#1A1A1A]">
              <span className="inline-block w-4 h-4 border-2 border-[#1B3B2B] rounded-sm flex-shrink-0" />
              <span>{it}</span>
            </div>
          ))}
        </div>
      </Card>

      {submitted ? (
        <>
          <Card>
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#1B3B2B] text-white mb-4">
                <Check size={28} />
              </div>
              <h2 className="font-serif-display text-3xl text-[#1B3B2B]">Welcome to the PRSVR Family!</h2>
              <p className="mt-3 text-[#4B5563] max-w-xl mx-auto">
                <strong>{data.student_name || "Your student"}</strong> is now officially on the
                roster for the upcoming academic year. A signed PDF of your enrollment
                agreement has been emailed to <strong>{enrollment?.email}</strong>. Our
                admissions team will be in touch within two business days regarding orientation week.
              </p>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/enrollment/${encodeURIComponent(enrollment?.email || "")}/pdf`}
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex items-center gap-2 bg-[#CD7F32] text-white hover:bg-[#B06A26] transition-colors rounded-sm px-7 py-3 font-semibold uppercase tracking-wider text-sm"
                data-testid="download-pdf-btn"
              >
                Download Signed PDF
              </a>
            </div>
          </Card>

          {/* PRSVR Ambassador Program */}
          <Card className="mt-6 border-l-4 border-l-[#CD7F32]">
            <div className="label-eyebrow text-[#CD7F32]">SAA Ambassador Program</div>
            <h3 className="font-serif-display text-2xl text-[#1B3B2B] mt-1">
              Elite performance is best achieved in an elite community.
            </h3>
            <p className="text-[#4B5563] mt-3 leading-relaxed">
              If you know another family who would benefit from our Scholar-Athlete model,
              share their contact below — or have them mention your name during their tour.
              For every family that successfully enrolls, we apply a{" "}
              <strong className="text-[#1B3B2B]">$250 credit toward your next month's tuition</strong>{" "}
              as our thank-you for helping us build this community.
            </p>

            <div className="mt-6 space-y-5">
              <CheckboxRow
                checked={data.wants_to_refer}
                onChange={(v) => set("wants_to_refer", v)}
                testId="check-wants-refer"
              >
                Yes — I'd like to refer a fellow athlete's family.
              </CheckboxRow>

              {data.wants_to_refer && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Referred Family — Parent Name" testId="outbound-referral-name">
                    <Input
                      value={data.outbound_referral_name}
                      onChange={(v) => set("outbound_referral_name", v)}
                      testId="outbound-referral-name-input"
                    />
                  </Field>
                  <Field label="Referred Family — Email" testId="outbound-referral-email">
                    <Input
                      type="email"
                      value={data.outbound_referral_email}
                      onChange={(v) => set("outbound_referral_email", v)}
                      testId="outbound-referral-email-input"
                    />
                  </Field>
                  <Field label="Student's Sport / Discipline" testId="outbound-referral-sport">
                    <Input
                      value={data.outbound_referral_sport}
                      onChange={(v) => set("outbound_referral_sport", v)}
                      placeholder="Optional"
                      testId="outbound-referral-sport-input"
                    />
                  </Field>
                  <Field label="Personal Note" testId="outbound-referral-note">
                    <Input
                      value={data.outbound_referral_note}
                      onChange={(v) => set("outbound_referral_note", v)}
                      placeholder="Optional — anything we should know"
                      testId="outbound-referral-note-input"
                    />
                  </Field>
                </div>
              )}
            </div>
          </Card>
        </>
      ) : (
        <>
          <Card>
            <div className="label-eyebrow">Summary</div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div><span className="text-[#4B5563]">Student:</span> <span className="font-semibold">{data.student_name || "—"}</span></div>
              <div><span className="text-[#4B5563]">Grade:</span> <span className="font-semibold">{data.student_grade || "—"}</span></div>
              <div><span className="text-[#4B5563]">Parent:</span> <span className="font-semibold">{data.parent_name || "—"}</span></div>
              <div><span className="text-[#4B5563]">Path:</span> <span className="font-semibold">{data.tier_path ? data.tier_path.replace("_", " ") : "—"}</span></div>
              <div><span className="text-[#4B5563]">Tier:</span> <span className="font-semibold">{data.tier_label || "—"}</span></div>
              <div><span className="text-[#4B5563]">Rate:</span> <span className="font-semibold">{data.tier_rate_display || "—"}</span></div>
              <div><span className="text-[#4B5563]">Enrollment Fee:</span> <span className="font-semibold">{enrollment?.payment_status === "paid" ? "Paid ($650)" : enrollment?.payment_status === "comped" ? `Covered · ${enrollment?.comp_family_label || "Comp"}` : "Pending ($650)"}</span></div>
              <div><span className="text-[#4B5563]">Email:</span> <span className="font-semibold">{enrollment?.email}</span></div>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <Card>
              <div className="label-eyebrow text-[#CD7F32]">Parent / Guardian Signature</div>
              <div className="font-serif-display text-2xl text-[#1B3B2B] mt-1 mb-4">{data.parent_name || "Parent Name"}</div>
              <SignaturePad
                value={data.parent_signature}
                onChange={(v) => {
                  set("parent_signature", v);
                  set("parent_sign_date", today);
                }}
                testId="parent-signature"
              />
              {data.parent_sign_date && (
                <div className="text-xs text-[#4B5563] mt-2">Signed {data.parent_sign_date}</div>
              )}
            </Card>

            <Card>
              <div className="label-eyebrow text-[#CD7F32]">Student Signature</div>
              <div className="font-serif-display text-2xl text-[#1B3B2B] mt-1 mb-4">{data.student_name || "Student Name"}</div>
              <SignaturePad
                value={data.student_signature}
                onChange={(v) => {
                  set("student_signature", v);
                  set("student_sign_date", today);
                }}
                testId="student-signature"
              />
              {data.student_sign_date && (
                <div className="text-xs text-[#4B5563] mt-2">Signed {data.student_sign_date}</div>
              )}
            </Card>
          </div>

          <div className="mt-8 flex justify-end">
            <button
              type="button"
              onClick={onSubmit}
              disabled={
                submitting ||
                !data.parent_signature ||
                !data.student_signature ||
                !(enrollment?.payment_status === "paid" || enrollment?.payment_status === "comped")
              }
              data-testid="submit-enrollment-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#1B3B2B] text-white hover:bg-[#132A13] disabled:opacity-50 transition-colors rounded-sm px-8 py-4 font-semibold uppercase tracking-wider text-sm"
            >
              {submitting ? "Submitting…" : "Submit Enrollment"}
            </button>
          </div>
          {!(enrollment?.payment_status === "paid" || enrollment?.payment_status === "comped") && (
            <div className="mt-4 p-4 border-2 border-[#991B1B]/30 bg-[#991B1B]/5 rounded-sm" data-testid="paywall-notice">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#991B1B] text-white flex items-center justify-center text-xs font-bold">!</div>
                <div className="text-sm">
                  <strong className="text-[#991B1B] uppercase tracking-widest text-xs">
                    Enrollment Fee Required
                  </strong>
                  <p className="mt-1 text-[#1A1A1A] leading-relaxed">
                    The <strong>$650 Setup &amp; Gear Fee</strong> must be paid before
                    your enrollment can be submitted. Please return to the
                    <strong> Tuition</strong> step to complete payment. Already paid?
                    Allow up to a minute for the system to confirm — if it still says
                    unpaid, contact <strong>records@prsvr.school</strong>.
                  </p>
                </div>
              </div>
            </div>
          )}
          {(!data.parent_signature || !data.student_signature) && (
            <p className="text-xs text-[#991B1B] mt-3 text-right">
              Both parent and student signatures are required to submit.
            </p>
          )}
        </>
      )}
    </div>
  );
}
